import { useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { ExtendedDataProvider } from '@src/types';

export type ChargePoint = { at: Date; total: number };

export type ChargeTotals = {
    num_of_charges?: number;
    sum_total_charge?: number;
    sum_total_processed?: number;
};

/**
 * The two summaries the charges endpoints offer over whatever the list is
 * filtered by: `graphs` for the series and `accumulated` for the totals.
 *
 * Both are asked for together and held here rather than in the chart and the
 * summary separately, so the two always describe the same filter and a change
 * to it costs one round of requests instead of two.
 */
const useChargeStatistics = (resource: string, filterValues: Record<string, any>) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const [points, setPoints] = useState<ChargePoint[]>([]);
    const [totals, setTotals] = useState<ChargeTotals | null>(null);
    const [loading, setLoading] = useState(true);

    // react-admin hands back a fresh filter object on every render, so the
    // effect keys off what is in it rather than which object it is
    const filterKey = JSON.stringify(
        Object.keys(filterValues || {})
            .sort()
            .map((key) => [key, filterValues[key]]),
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        Promise.all([
            dataProvider.getAction(resource, { verb: 'graphs', filter: filterValues }),
            dataProvider.getAction(resource, { verb: 'accumulated', filter: filterValues }),
        ])
            .then(([graphs, accumulated]: any[]) => {
                if (cancelled) return;

                const series: ChargePoint[] = Object.values(graphs.data || {})
                    .map((bucket: any) => ({
                        at: new Date(bucket.start_min),
                        total: Number(bucket.total_charge) || 0,
                    }))
                    // the endpoint groups by period but does not promise an order,
                    // and a line drawn out of order reads as noise
                    .sort((a, b) => a.at.getTime() - b.at.getTime());

                setPoints(series);
                setTotals(accumulated.data || null);
            })
            .catch(() => {
                if (cancelled) return;
                setPoints([]);
                setTotals(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // filterValues is covered by filterKey
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resource, filterKey, dataProvider]);

    return useMemo(() => ({ points, totals, loading }), [points, totals, loading]);
};

export default useChargeStatistics;
